import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Card, Col, Container, Row } from "react-bootstrap";
import React, { Fragment, useEffect, useState } from "react";
import {
  createNewCompany,
  fetchCompanyList,
  fetchEditCompany,
} from "../actions/company";
import { useNavigate, useParams } from "react-router-dom";

import Breadcrumb from "../components/Admin/Reusable/Breadcrumb";
import InputField from "../components/Admin/Reusable/InputField";
import SelectStatus from "../components/Admin/Reusable/Form/SelectStatus";
import SubmitButtonForm from "../components/Admin/Reusable/Form/SubmitButtonForm";
import { basicFormValidationHandler } from "../utils";
import { fetchCountryFormList } from "../actions/country";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import FormSystemInformation from "../components/Admin/Reusable/FormSystemInformation";
import FormTitleMain from "../components/Admin/Reusable/FormTitleMain";

const CompanyAdd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { t } = useTranslation();
  //breadcrumb
  const translateForm = "companiesForm";
  const translateModule = "companies";
  const pageType = id ? "Edit" : "Add";
  const breadcrumbItems = [
    { label: t(`breadcrumb.${translateModule}`) },
    {
      label: t(`breadcrumb.${translateModule}${pageType}`),
      active: true,
    },
  ];

  const initialState = {
    name: "",
    address: "",
    pib: "",
    email: "",
    phone: "",
    identificationNumber: "",
    countryId: "",
    status: "ACTIVE",
  };

  const [data, setData] = useState(initialState);
  const [errors, setErrors] = useState({});

  const { countryFormList } = useSelector((state) => state.countryReducer);
  const { companyDetail } = useSelector((state) => state.companyReducer);

  const handleInputChange = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  //pozvati podatke za drzave kako bi izlistali u selektu
  useEffect(() => {
    dispatch(fetchCountryFormList());
  }, []);

  useEffect(() => {
    if (!id) {
      // Ako `id` ne postoji, očisti stanje za novu kreaciju
      setData(initialState);
    } else {
      // Ako `id` postoji, učitaj podatke za korisnika
      dispatch(fetchCompanyList(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (companyDetail && id) {
      setData({
        ...data,
        name: companyDetail.name || initialState.name,
        address: companyDetail.address || initialState.address,
        pib: companyDetail.pib || initialState.pib,
        email: companyDetail.email || initialState.email,
        phone: companyDetail.phone || initialState.phone,
        countryId: companyDetail.countryId || initialState.countryId,
        identificationNumber:
          companyDetail.identificationNumber ||
          initialState.identificationNumber,
        status: companyDetail.status || initialState.status,
        createdAt: companyDetail.createdAt || "",
        updatedAt: companyDetail.updatedAt || "",
      });
    }
  }, [companyDetail, id]);

  const clientValidationHandler = () => {
    const { name, countryId } = data;

    let errorsObject = {};

    errorsObject = basicFormValidationHandler({
      name,
      countryId,
    });

    setErrors(errorsObject);

    if (!isEmpty(errorsObject))
      toast.error("Morate popuniti sva obavezna polja", { theme: "colored" });
    return !isEmpty(errorsObject);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (clientValidationHandler()) {
      return;
    }

    if (id) {
      data.id = id;
      dispatch(fetchEditCompany(data, navigate));
    } else {
      dispatch(createNewCompany(data, navigate));
    }
  };

  useEffect(() => {
    if (countryFormList?.length == 1) {
      setData((prevData) => ({
        ...prevData,
        countryId: countryFormList[0].id,
      }));
    }
  }, [countryFormList]);

  return (
    <Fragment>
      <Breadcrumb
        breadcrumbItems={breadcrumbItems}
        type={translateModule}
        id={id}
        pageType={pageType}
        extraTitle={companyDetail && companyDetail.name}
      />
      <Container>
        <Row>
          <Col lg={12}>
            <form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col lg={12}>
                  <div className="text-end mb-2">
                    <SubmitButtonForm id={id} />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg={9}>
                  <Card>
                    <Card.Header>
                      <FormTitleMain />
                    </Card.Header>
                    <Card.Body>
                      <div className="product-holder-admin-left-side-text">
                        <Row>
                          <Col lg={5} md={12} xs={12} className="mb-4">
                            <InputField
                              label={t("companyForm.name")}
                              name="name"
                              value={data.name}
                              onChange={handleInputChange}
                              error={errors}
                              errorsHandler={setErrors}
                              fullWidth
                              size="small"
                              required
                            />
                          </Col>
                          <Col lg={3} md={12} xs={12} className="mb-4">
                            <FormControl
                              fullWidth
                              required
                              size="small"
                              error={errors?.countryId}
                              disabled={countryFormList?.length == 1}
                            >
                              <InputLabel id="demo-simple-select-label">
                                {t("companyForm.country")}
                              </InputLabel>
                              <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={data.countryId}
                                label={t("companyForm.country")}
                                name="countryId"
                                onChange={handleInputChange}
                                size="small"
                              >
                                {countryFormList?.map((item, key) => (
                                  <MenuItem value={item.id} key={key}>
                                    {item.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              {errors?.countryId && (
                                <FormHelperText>
                                  {t("common.requiredField")}
                                </FormHelperText>
                              )}
                            </FormControl>
                          </Col>
                          <Col lg={4} md={12} xs={12} className="mb-4">
                            <InputField
                              label={t("companyForm.identificationNumber")}
                              name="identificationNumber"
                              value={data.identificationNumber}
                              onChange={handleInputChange}
                              error={errors}
                              errorsHandler={setErrors}
                              fullWidth
                              size="small"
                            />
                          </Col>
                          <Col lg={8} md={12} xs={12} className="mb-4">
                            <InputField
                              label={t("companyForm.address")}
                              name="address"
                              value={data.address}
                              onChange={handleInputChange}
                              error={errors}
                              errorsHandler={setErrors}
                              fullWidth
                              size="small"
                            />
                          </Col>
                          <Col lg={4} md={12} xs={12} className="mb-4">
                            <InputField
                              label={t("companyForm.pib")}
                              name="pib"
                              value={data.pib}
                              onChange={handleInputChange}
                              error={errors}
                              errorsHandler={setErrors}
                              fullWidth
                              size="small"
                            />
                          </Col>
                          <Col lg={6} md={12} xs={12} className="mb-4">
                            <InputField
                              label={t("companyForm.email")}
                              name="email"
                              type={"email"}
                              value={data.email}
                              onChange={handleInputChange}
                              error={errors}
                              errorsHandler={setErrors}
                              fullWidth
                              size="small"
                            />
                          </Col>
                          <Col lg={6} md={12} xs={12} className="mb-4">
                            <InputField
                              label={t("companyForm.phone")}
                              type="text"
                              name="phone"
                              value={data.phone}
                              onChange={handleInputChange}
                              error={errors}
                              errorsHandler={setErrors}
                              fullWidth
                              size="small"
                            />
                          </Col>
                        </Row>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={3}>
                  <Row>
                    <Col lg={12}>
                      <SelectStatus
                        data={data}
                        handleInputChange={handleInputChange}
                      />
                    </Col>
                    {id && (
                      <Col lg={12}>
                        <FormSystemInformation data={data} />
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default CompanyAdd;
